import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building2, FileText, Trash2, Upload, User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type {
  CreateCompanyPayload,
  UpdateCompanyPayload,
} from "@/lib/http/company";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { createCompanySchema, updateCompanySchema } from "@/lib/http/company";
import { api } from "@/lib/http";
import {
  companyQueryOptions,
  templatePoliciesQueryOptions,
} from "@/lib/queries/company";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useResourceAccessPermission } from "@/lib/perms";

export const Route = createFileRoute("/_app/company")({
  component: RouteComponent,
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(companyQueryOptions());
    context.queryClient.ensureQueryData(templatePoliciesQueryOptions());
  },
  staticData: {
    permissionGuards: [
      {
        resource: "companies",
      },
    ],
  },
});

function RouteComponent() {
  const companyQuery = useQuery(companyQueryOptions());

  // Show loading state while fetching
  if (companyQuery.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  // If query succeeded, show company info
  // Otherwise show registration
  return companyQuery.isSuccess ? <CompanyInfo /> : <CompanyRegistration />;
}

function CompanyInfo() {
  const companyQuery = useSuspenseQuery(companyQueryOptions());
  const queryClient = useQueryClient();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const canUpdate = useResourceAccessPermission("companies", ["update"]);

  const form = useForm<UpdateCompanyPayload>({
    resolver: zodResolver(updateCompanySchema),
    defaultValues: {
      company_name: companyQuery.data.company_name,
      description: companyQuery.data.description,
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateCompanyPayload) => {
      return api.company.updateCompany({
        company_name: data.company_name,
        description: data.description,
      });
    },
    onSuccess: () => {
      toast.success("Company updated successfully");
      queryClient.invalidateQueries({ queryKey: ["company"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to update company: ${error.message}`);
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => api.company.updateCompanyLogo(file),
    onSuccess: () => {
      toast.success("Company logo updated successfully");
      queryClient.invalidateQueries({ queryKey: ["company"] });
    },
    onError: async (error) => {
      let message = error.message;
      if ("response" in error && error.response instanceof Response) {
        try {
          const body = (await error.response.json()) as { detail?: unknown };
          if (body.detail) {
            message = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
          }
        } catch {}
      }
      toast.error(message);
      setLogoPreview(null);
    },
  });

  const deleteLogoMutation = useMutation({
    mutationFn: () => api.company.deleteCompanyLogo(),
    onSuccess: () => {
      toast.success("Company logo deleted successfully");
      setLogoPreview(null);
      queryClient.invalidateQueries({ queryKey: ["company"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete logo: ${error.message}`);
    },
  });

  const onSubmit = (data: UpdateCompanyPayload) => {
    updateMutation.mutate(data);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      uploadLogoMutation.mutate(file);
    }
  };

  return (
    <div className="w-full p-4 md:p-6">
      <h1 className="mb-6 text-2xl font-bold">Company information</h1>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Details</CardTitle>
              <CardDescription>Update your company details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:gap-8">
                <div className="flex shrink-0 flex-col items-center">
                  <div className="mb-4 flex size-32 items-center justify-center overflow-hidden rounded-full border-2 border-gray-200 bg-gray-50">
                    {logoPreview || companyQuery.data.company_logo ? (
                      <img
                        src={logoPreview || companyQuery.data.company_logo}
                        alt="Company logo"
                        className="size-full object-cover"
                      />
                    ) : (
                      <User className="size-16 text-gray-400" />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <label htmlFor="logo-upload">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        asChild
                        className="border-primary text-primary hover:bg-primary/5"
                      >
                        <span className="cursor-pointer">
                          <Upload className="size-3.5" />
                          Upload Logo
                        </span>
                      </Button>
                    </label>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2 text-[#535862] dark:text-white"
                          disabled={
                            deleteLogoMutation.isPending ||
                            !(logoPreview || companyQuery.data.company_logo)
                          }
                        >
                          <Trash2 className="size-3.5" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Delete company logo?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete the company logo?
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive hover:bg-destructive/90 dark:text-white"
                            onClick={() => deleteLogoMutation.mutate()}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                  <p className="mt-2 text-xs text-muted-foreground">
                    We support PNG, JPG, or WebP
                  </p>
                </div>

                <div className="flex-1 space-y-4">
                  <FormField
                    control={form.control}
                    name="company_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter company name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Enter company description"
                            rows={5}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {canUpdate.data && (
            <div className="mt-4 flex justify-end">
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}

function CompanyRegistration() {
  const policiesQuery = useSuspenseQuery(templatePoliciesQueryOptions());
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [usersFile, setUsersFile] = useState<File | null>(null);

  const canCreate = useResourceAccessPermission("companies", ["create"]);

  const form = useForm<CreateCompanyPayload>({
    resolver: zodResolver(createCompanySchema),
    defaultValues: {
      company_name: "",
      description: "",
      policies: [],
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateCompanyPayload) => {
      return api.company.createCompany(data);
    },
    onSuccess: () => {
      toast.success("Company registered successfully");
      queryClient.invalidateQueries({ queryKey: ["company"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to register company: ${error.message}`);
    },
  });

  const onSubmit = (data: CreateCompanyPayload) => {
    createMutation.mutate(data);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      form.setValue("company_logo", file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUsersFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUsersFile(file);
      form.setValue("users_file", file);
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent =
      "full_name,email,password,role_name,department_name\nJohn Doe,john@example.com,password123,Admin,Engineering";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!showForm) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center px-4">
        <div className="text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-2xl bg-gray-100 p-6">
              <Building2 className="size-16 text-gray-400" />
            </div>
          </div>
          <h1 className="mb-3 text-2xl font-bold md:text-3xl">
            No Company Registered
          </h1>
          <p className="mb-6 text-sm text-gray-500 md:text-base">
            You haven't registered a company yet. Register one to get started.
          </p>
          {canCreate.data && (
            <Button onClick={() => setShowForm(true)} size="lg">
              <Building2 />
              Register Company
            </Button>
          )}
        </div>
      </div>
    );
  }

  const policyOptions = Object.keys(policiesQuery.data);

  return (
    <div className="w-full p-4 md:p-6">
      <div className="mb-6 md:mb-8">
        <div className="mb-2 flex items-center gap-2 md:gap-3">
          <Building2 className="size-6 shrink-0 text-primary md:size-8" />
          <h1 className="text-2xl font-bold md:text-3xl">
            Company Registration
          </h1>
        </div>
        <p className="text-sm text-muted-foreground md:text-base">
          Register your company and set up initial governance policies.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>
                Provide basic details about your company
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="company_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Polygraf Inc" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Enterprise Grade"
                        rows={4}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Company Logo</CardTitle>
              <CardDescription>
                Upload your company logo (PNG, JPG, or SVG)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {logoPreview && (
                <div className="mb-4 flex justify-center">
                  <div className="flex size-32 items-center justify-center overflow-hidden rounded-lg bg-gray-100 p-4">
                    <img
                      src={logoPreview}
                      alt="Company logo preview"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="logo-upload">
                  <Button type="button" variant="outline" asChild>
                    <span className="cursor-pointer">
                      <Upload />
                      {logoFile ? "Change Logo" : "Upload Logo"}
                    </span>
                  </Button>
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleLogoChange}
                />
                {logoFile && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {logoFile.name}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Import Users Directory</CardTitle>
              <CardDescription>
                Upload a CSV file containing your users directory. This will be
                appended to any existing users. Expected format: full_name,
                email, password, role_name, department_name
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="users-upload">
                  <Button type="button" variant="outline" asChild>
                    <span className="cursor-pointer">
                      <FileText />
                      {usersFile ? "Change CSV" : "Upload CSV"}
                    </span>
                  </Button>
                </label>
                <input
                  id="users-upload"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleUsersFileChange}
                />
                {usersFile && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {usersFile.name}
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-border bg-muted/50 p-4">
                <p className="mb-2 text-sm font-medium">
                  CSV Format Requirements:
                </p>
                <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                  <li>full_name: Full name of the user</li>
                  <li>email: Valid email address</li>
                  <li>password: Initial password</li>
                  <li>role_name: User's role</li>
                  <li>department_name: User's department</li>
                </ul>
                <Button
                  type="button"
                  variant="link"
                  className="mt-3 h-auto p-0 text-primary"
                  onClick={downloadCSVTemplate}
                >
                  <FileText className="size-4" />
                  Download sample CSV template
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pre-made Compliance Policies</CardTitle>
              <CardDescription>
                Select the compliance policies you want to apply company-wide
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="policies"
                render={() => (
                  <FormItem>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {policyOptions.map((policyKey) => (
                        <FormField
                          key={policyKey}
                          control={form.control}
                          name="policies"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={policyKey}
                                className="flex flex-row items-start space-y-0 space-x-3 rounded-lg border border-border p-4"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={
                                      field.value?.includes(policyKey) || false
                                    }
                                    onCheckedChange={(checked) => {
                                      const currentValue = field.value || [];
                                      if (checked) {
                                        field.onChange([
                                          ...currentValue,
                                          policyKey,
                                        ]);
                                      } else {
                                        field.onChange(
                                          currentValue.filter(
                                            (value) => value !== policyKey,
                                          ),
                                        );
                                      }
                                    }}
                                  />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                  <FormLabel className="font-semibold">
                                    {policyKey}
                                  </FormLabel>
                                </div>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
            {canCreate.data && (
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending
                  ? "Registering..."
                  : "Register Company"}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
